import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import Link from "@mui/material/Link"
import Typography from "@mui/material/Typography"

import Column from "../../../common/components/Column"
import acuitySyncScreenshot from "../../../static/images/acuity-sync-with-other-calendars.png"
import ProfileColumn from "./ProfileColumn"

const ACUITY_APP_URL = "https://secure.acuityscheduling.com/login.php"
const BROWSER_CACHE_GUIDE_URL =
  "https://help.acuityscheduling.com/hc/en-us/articles/18660661668237-Clearing-your-browser-cache"
const ACUITY_GOOGLE_CALENDAR_GUIDE_URL =
  "https://help.acuityscheduling.com/hc/en-us/articles/16676868807181-Syncing-your-calendar"

const listSx = {
  m: 0,
  pl: 2.5,
  textAlign: "left",
  "& li": { mb: 1.5, "&:last-child": { mb: 0 } },
}

export default function AcuityCalendarTab() {
  return (
    <ProfileColumn sx={{ gap: 3 }}>
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardHeader
          title={
            <Typography variant="h5" component="div" fontWeight={700}>
              <Box
                component="span"
                sx={{
                  textDecoration: "underline",
                  textUnderlinePosition: "from-font",
                  textDecorationThickness: "2px",
                }}
              >
                Acuity Scheduling
              </Box>{" "}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                (For Active Recruiting Campaigns Only)
              </Typography>
            </Typography>
          }
          sx={{
            textAlign: "left",
            alignItems: "flex-start",
            pb: 1,
            "& .MuiCardHeader-content": { overflow: "visible" },
          }}
        />
        <CardContent
          sx={{
            pt: 0,
            px: 2,
            pb: 2,
            "&:last-child": { pb: 2 },
          }}
        >
          <Column sx={{ gap: 2, alignItems: "stretch", ml: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "left" }}
            >
              Calendar scheduling with Acuity is a complimentary feature for
              active SmartSetter Recruiting Campaigns.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "left" }}
            >
              We strongly recommend syncing your calendar to prevent calendar
              conflicts and double-bookings.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              href={ACUITY_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                whiteSpace: "normal",
                textAlign: "left",
              }}
            >
              You can find your Acuity syncing settings right here.
            </Button>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "left" }}
            >
              When prompted, use the following logins for your account:
            </Typography>
            <Box
              component="dl"
              sx={{
                m: 0,
                pl: 0,
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: 2,
                rowGap: 0.5,
                textAlign: "left",
              }}
            >
              <Typography
                component="dt"
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                User ID:
              </Typography>
              <Typography component="dd" variant="body2" color="text.secondary">
                Enter your email
              </Typography>
              <Typography
                component="dt"
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Password:
              </Typography>
              <Typography component="dd" variant="body2" color="text.secondary">
                5mart5etter
              </Typography>
            </Box>
            <Box component="ol" sx={listSx}>
              <Typography component="li" variant="body2" color="text.secondary">
                Once logged in, select{" "}
                <strong>Sync with other calendars</strong> on the left side menu
                bar.
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Select the type of calendar you want to sync to (Google,
                Outlook, iCal etc.)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Once you have selected the calendar type, use the drop-down menu
                to select the email address (associated with your calendar) you
                would like to sync.
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Click <strong>SAVE</strong> (don&apos;t skip this step!) and
                make sure it then says <strong>Active</strong> next to the
                calendar type you have selected.
              </Typography>
            </Box>
            <Box
              component="img"
              src={acuitySyncScreenshot}
              alt="Acuity Sync with Other Calendars menu with Google Calendar marked Active"
              sx={{
                display: "block",
                maxWidth: 220,
                width: "100%",
                height: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{ textAlign: "left", fontWeight: 600 }}
            >
              Having trouble completing your sync?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "left" }}
            >
              Follow these 3 quick steps:
            </Typography>
            <Box component="ol" sx={listSx}>
              <Typography component="li" variant="body2" color="text.secondary">
                Go to <strong>Sync with other calendars</strong> &gt;{" "}
                <strong>Google Calendar</strong> &gt;{" "}
                <strong>Disconnect Google calendar account</strong>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Clear the cache on your browser (
                <Link
                  href={BROWSER_CACHE_GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  guide here
                </Link>
                )
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Go to <strong>Sync with other calendars</strong> &gt;{" "}
                <strong>Google Calendar</strong> &gt;{" "}
                <strong>Connect Google account again</strong> (
                <Link
                  href={ACUITY_GOOGLE_CALENDAR_GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  guide here
                </Link>
                )
              </Typography>
            </Box>
          </Column>
        </CardContent>
      </Card>
    </ProfileColumn>
  )
}
